import * as React from 'react'
import type { TierConfig, TierLevel, FeatureFlags, TierLayoutConfig, TierThemeExtensions } from '@/lib/api'
import { mergeTierConfig, getDefaultTierConfig } from '@/lib/tier-defaults'

// ─────────────────────────────────────────────────────────────────────────────
// Tier Context Types
// ─────────────────────────────────────────────────────────────────────────────

export type TierContextValue = {
  /** Current tier level */
  tier: TierLevel
  /** Full tier configuration */
  config: TierConfig
  /** Feature flags for the current tier */
  features: FeatureFlags
  /** Layout configuration for the current tier */
  layout: TierLayoutConfig
  /** Optional theme extensions for premium tiers */
  themeExtensions?: TierThemeExtensions

  // Convenience booleans
  /** True if tier is 'premium' or 'enterprise' */
  isPremium: boolean
  /** True if tier is 'enterprise' */
  isEnterprise: boolean
  /** True if tier is 'free' */
  isFree: boolean
  /** True if subscription has expired (falls back to free tier) */
  isExpired: boolean

  /** Check if a specific feature is enabled */
  hasFeature: (feature: keyof FeatureFlags) => boolean
}

// ─────────────────────────────────────────────────────────────────────────────
// Context Definition
// ─────────────────────────────────────────────────────────────────────────────

const TierContext = React.createContext<TierContextValue | undefined>(undefined)

// ─────────────────────────────────────────────────────────────────────────────
// Provider Component
// ─────────────────────────────────────────────────────────────────────────────

export function TierProvider({
  tierConfig,
  children
}: {
  tierConfig?: Partial<TierConfig>
  children: React.ReactNode
}) {
  const value = React.useMemo<TierContextValue>(() => {
    const config = mergeTierConfig(tierConfig)
    const now = new Date()
    const expiryDate = config.expiresAt ? new Date(config.expiresAt) : null
    const isExpired = expiryDate ? now > expiryDate : false

    // If expired, fall back to free tier features
    const effectiveConfig = isExpired ? getDefaultTierConfig('free') : config

    return {
      tier: effectiveConfig.tier,
      config: effectiveConfig,
      features: effectiveConfig.features,
      layout: effectiveConfig.layout,
      themeExtensions: effectiveConfig.themeExtensions,

      isPremium: effectiveConfig.tier === 'premium' || effectiveConfig.tier === 'enterprise',
      isEnterprise: effectiveConfig.tier === 'enterprise',
      isFree: effectiveConfig.tier === 'free',
      isExpired,

      hasFeature: (feature: keyof FeatureFlags) => {
        const val = effectiveConfig.features[feature]
        if (typeof val === 'boolean') return val
        if (typeof val === 'number') return val !== 0
        return false
      },
    }
  }, [tierConfig])

  return <TierContext.Provider value={value}>{children}</TierContext.Provider>
}

// ─────────────────────────────────────────────────────────────────────────────
// Hook
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Access the current tier context.
 * Must be used within a TierProvider.
 */
export function useTier(): TierContextValue {
  const ctx = React.useContext(TierContext)
  if (!ctx) {
    throw new Error('useTier must be used within TierProvider')
  }
  return ctx
}
